import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess"; // Icon for collapse
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SelectChangeEvent } from "@mui/material/Select";
import { PageChangeEvent } from "@react-pdf-viewer/core";
import React, { useCallback, useContext, useEffect, useState } from "react";
import apiClient from "../../utils/api";
import { ChatContext } from "./ChatContainer";
import { DocumentIdsForChat } from "../../services";
import { PDFViewerForStudents } from "../Shared/PDFViewer/PDFViewerForStudent";
import { useSaveInteraction } from "../../utils/useSaveInteraction";
import useGetPdfById from "./useGetPdfById";
import { useNavigate } from "react-router-dom";

const ChatPDF: React.FC = () => {
  const [pdfList, setPdfList] = useState<DocumentIdsForChat[]>();
  const getPdfById = useGetPdfById();
  // const [pdfContent, setPdfContent] = useState<string>("");
  const { saveInteraction } = useSaveInteraction();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true); // State for tracking expansion

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev); // Toggle the expanded state
  };

  const {
    class: selectedClass,
    currentPDFID: selectedPDFID,
    setCurrentPDFID: setSelectedPDFID,
    currentPdfName: selectedPdfName,
    setCurrentPdfName: setSelectedPdfName,
    currentPDFMaterialType: selectedPDFMaterialType,
    setCurrentPDFMaterialType: setSelectedPDFMaterialType,
    setCurrentPDFPage,
    pdfContent,
    setPdfContent,
    multipleCourses,
  } = useContext(ChatContext);
  // get the student class ID from the API
  // use admin endpoint for now

  useEffect(() => {
    const fetchClassPDFs = async () => {
      if (selectedClass === undefined) return;
      if (!selectedClass.classID) return;
      // get the pdfs from API
      // const res = await apiClient.classControllerGetDocumentList({
      //   classID: selectedClass.classID,
      // });
      const res = await apiClient.classControllerGetDocumentListIds({
        classID: selectedClass.classID,
      });
      if (res) {
        console.log("documents:", res);
        setPdfList(res.documentIds);
      }
      const documents = res.documentIds;

      if (!documents || documents.length === 0) return;

      if (!documents[0]) return;
      // set the first pdf as the default pdf
      if (
        setSelectedPdfName &&
        setSelectedPDFID &&
        setSelectedPDFMaterialType &&
        setPdfContent
      ) {
        // set last recitation pdf as the default pdf without changing list
        const reversedClassPDFs = [...documents].reverse();
        const lastRecitationPDF = reversedClassPDFs.find(
          (pdf) => pdf.materialType === "recitation"
        );
        if (lastRecitationPDF) {
          setSelectedPdfName(lastRecitationPDF.fileName || "");
          setSelectedPDFID(lastRecitationPDF.id || "");
          setSelectedPDFMaterialType(lastRecitationPDF.materialType || "");
          const pdfContent = await getPdfById(lastRecitationPDF.id);
          setPdfContent(pdfContent.documentContent);
        } else {
          setSelectedPdfName(documents[0].fileName || "");
          setSelectedPDFID(documents[0].id || "");
          setSelectedPDFMaterialType(documents[0].materialType || "");
          const pdfContent = await getPdfById(documents[0].id);
          setPdfContent(pdfContent.documentContent);
        }
      }
    };

    fetchClassPDFs();
  }, [
    selectedClass,
    setSelectedPdfName,
    setSelectedPDFID,
    setSelectedPDFMaterialType,
    setPdfContent,
    getPdfById,
  ]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    if (typeof event.target.value === "string") {
      const selectedPDF = pdfList?.find(
        (c) => c.fileName === event.target.value
      );
      if (setSelectedPdfName) {
        console.log("event.target", event.target);
        setSelectedPdfName(event.target.value);
      }
      if (setSelectedPDFID) {
        setSelectedPDFID(selectedPDF?.id || "");
      }
      if (setSelectedPDFMaterialType) {
        const selectedPDF = pdfList?.find(
          (c) => c.fileName === event.target.value
        );
        setSelectedPDFMaterialType(selectedPDF?.materialType || "");
      }
      if (setPdfContent) {
        setPdfContent("");
        const pdfContent = await getPdfById(selectedPDF?.id || "");
        setPdfContent(pdfContent.documentContent);
      }
    }
  };

  const onPageChangeCallback = useCallback(
    (e: PageChangeEvent) => {
      console.log("page changed", e);
      // Save the pdf scrolling interaction
      saveInteraction({
        name: "frontend-interaction-pdf-change",
        context: {
          pdfID: selectedPDFID,
          pdfPage: e.currentPage + 1,
          pdfMaterialType: selectedPDFMaterialType,
        },
      });
      console.log("current page", e.currentPage + 1);
      if (setCurrentPDFPage) setCurrentPDFPage(e.currentPage);
    },
    [setCurrentPDFPage, selectedPDFID, selectedPDFMaterialType, saveInteraction]
  );

  return (
    <Box overflow="auto">
      {/* Foldable container for the class name and "Change the class" button */}
      {selectedClass && multipleCourses && (
        <Box
          display="flex"
          flexDirection="column"
          mb={1} // Add some margin-bottom to create space below the container
          p={isExpanded ? 0 : 0} // Adjust padding when expanded or collapsed
          height={isExpanded ? "60px" : "30px"} // Change height when collapsed
          width="100%" // Keep the full width
          border="1px solid #ccc"
          borderRadius="8px"
          boxShadow="0 1px 3px rgba(0,0,0,0.2)"
          overflow="hidden" // Hide content when collapsed
        >
          {isExpanded && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginRight={5}
              marginLeft={3}
              mt={1}
              mb={1}
            >
              <Typography variant="h6">
                Class: {selectedClass.className || "Unknown Class"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate("/chat", { replace: true });
                  navigate(0); // This forces a reload
                }} // Redirect to the class selection page
              >
                Change the class
              </Button>
            </Box>
          )}

          {/* Shrunk IconButton in the bottom right */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            position="relative"
            mt={isExpanded ? 1 : 0} // Margin on top for spacing when expanded
            height={isExpanded ? "auto" : "30px"} // Adjust icon size when collapsed
            width="100%"
          >
            <IconButton
              size="small" // Make the button smaller
              onClick={toggleExpand}
              sx={{ position: "absolute", bottom: 0, right: 0 }} // Place at the bottom right
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box>
            {pdfList && pdfList.length > 0 ? (
              <FormControl fullWidth variant="filled">
                <InputLabel
                  id="demo-simple-select-label"
                  style={{ fontSize: "20px" }}
                >
                  Chat With a Lecture
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedPdfName}
                  label="Age"
                  onChange={handleChange}
                >
                  {pdfList
                    .filter((pdf) => pdf.materialType === "class materials")
                    .map((pdf, index) => {
                      return (
                        <MenuItem key={index} value={pdf.fileName}>
                          {pdf.fileName}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            ) : (
              <Skeleton />
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box>
            {pdfList && pdfList.length > 0 ? (
              <FormControl fullWidth variant="filled">
                <InputLabel
                  id="demo-simple-select-label"
                  style={{ fontSize: "20px" }}
                >
                  Chat With a Recitation Worksheet
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedPdfName}
                  label="Age"
                  onChange={handleChange}
                >
                  {pdfList
                    .filter((pdf) => pdf.materialType === "recitation")
                    .map((pdf, index) => {
                      return (
                        <MenuItem key={index} value={pdf.fileName}>
                          {pdf.fileName}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            ) : (
              <Skeleton />
            )}
          </Box>
        </Grid>
      </Grid>
      <PDFViewerForStudents
        pdfFile={pdfContent ?? ""}
        studentMode
        onPageChangeCallback={onPageChangeCallback}
        isExpanded={isExpanded}
      />
    </Box>
  );
};

export default ChatPDF;
